import type { GatekeeperPolicyAssets } from './policyEngine'

const textDecoder = new TextDecoder()

/** Default remote Gatekeeper OPA bundle (gzip tar). */
export const DEFAULT_GATEKEEPER_BUNDLE_URL =
  'https://assets.flyout.labra.io/wasm_bundle.tar.gz'

const STATIC_OPA_BASE = '/opa'
const STATIC_POLICY_BASE = `${STATIC_OPA_BASE}/gatekeeper`
/** Optional human-readable policy; canonical source is `public/opa/authz.rego`. */
const STATIC_REGO_PATH = `${STATIC_OPA_BASE}/authz.rego`

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new Uint8Array(buffer))
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/** When set (e.g. in CI/prod env), fetched gzip bytes must match this hex digest. */
export async function assertGatekeeperBundleSha256(
  gzipBuffer: ArrayBuffer,
  expectedHex: string
): Promise<void> {
  const normalized = expectedHex.trim().toLowerCase()
  if (!normalized) return
  const actual = await sha256Hex(gzipBuffer)
  if (actual !== normalized) {
    throw new Error(
      `Gatekeeper bundle SHA-256 mismatch: expected ${normalized}, got ${actual}`
    )
  }
}
function patchManifestModulePath(manifest: Record<string, unknown>): void {
  const wasm = manifest.wasm as Record<string, unknown> | undefined
  const entrypoints = wasm?.entrypoints as Record<string, unknown> | undefined
  const policy = entrypoints?.policy as Record<string, unknown> | undefined
  if (policy && policy.module === '/policy.wasm') {
    policy.module = 'policy.wasm'
  }
}

function parseOctalField(
  header: Uint8Array,
  start: number,
  length: number
): number {
  const slice = header.subarray(start, start + length)
  let s = textDecoder.decode(slice).replace(/\0/g, '').trim()
  // GNU tar can use base-256 for large numbers; bundle files are small.
  if (s.charCodeAt(0) === 0x80) return 0
  const n = parseInt(s, 8)
  return Number.isFinite(n) ? n : 0
}

/**
 * Parse a POSIX ustar tar from bytes into a map of path -> file contents.
 */
export function parseTarArchive(tarBytes: Uint8Array): Map<string, Uint8Array> {
  const files = new Map<string, Uint8Array>()
  let offset = 0

  while (offset + 512 <= tarBytes.length) {
    const header = tarBytes.subarray(offset, offset + 512)
    offset += 512

    if (header.every(b => b === 0)) {
      break
    }

    let name = textDecoder.decode(header.subarray(0, 100)).replace(/\0/g, '')
    const size = parseOctalField(header, 124, 12)
    const typeflag = textDecoder.decode(header.subarray(156, 157))

    // ustar extended path
    if (name.length === 0 || name === './' || name === '.') {
      // skip weird entries
    }
    const prefix = textDecoder
      .decode(header.subarray(345, 500))
      .replace(/\0/g, '')
    if (prefix) {
      name = `${prefix.replace(/\/$/, '')}/${name.replace(/^\//, '')}`
    }

    const isDir = typeflag === '5' || name.endsWith('/')
    const contentStart = offset
    const contentEnd = offset + size
    if (contentEnd > tarBytes.length) {
      throw new Error('Invalid tar: truncated file payload')
    }

    if (!isDir && name) {
      const normalized = name.replace(/^\.\//, '')
      files.set(normalized, tarBytes.subarray(contentStart, contentEnd))
    }

    offset = contentEnd
    offset = Math.ceil(offset / 512) * 512
  }

  return files
}

export async function gunzipToUint8Array(
  gzipBuffer: ArrayBuffer
): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('DecompressionStream is not available in this environment')
  }
  const ds = new DecompressionStream('gzip')
  const chunk = new Uint8Array(gzipBuffer)
  const readable = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(chunk)
      controller.close()
    },
  })
  const out = await new Response(readable.pipeThrough(ds)).arrayBuffer()
  return new Uint8Array(out)
}

function findFile(
  files: Map<string, Uint8Array>,
  basename: string
): Uint8Array {
  for (const [path, data] of files) {
    if (path === basename || path.endsWith(`/${basename}`)) {
      return data
    }
  }
  throw new Error(`Bundle missing required file: ${basename}`)
}

export async function loadPolicyAssetsFromTarGzBuffer(
  gzipBuffer: ArrayBuffer
): Promise<GatekeeperPolicyAssets> {
  const tarBytes = await gunzipToUint8Array(gzipBuffer)
  const files = parseTarArchive(tarBytes)

  const wasmBytes = findFile(files, 'policy.wasm')
  const wasm = wasmBytes.buffer.slice(
    wasmBytes.byteOffset,
    wasmBytes.byteOffset + wasmBytes.byteLength
  ) as ArrayBuffer

  const manifestText = textDecoder.decode(findFile(files, '.manifest'))
  const manifest = JSON.parse(manifestText) as Record<string, unknown>
  patchManifestModulePath(manifest)

  const dataText = textDecoder.decode(findFile(files, 'data.json'))
  const data = JSON.parse(dataText) as Record<string, unknown>

  let rego: string | null = null
  try {
    rego = textDecoder.decode(findFile(files, 'authz.rego'))
  } catch {
    rego = null
  }

  return {
    wasm,
    manifest,
    data,
    rego,
    loadedAt: Date.now(),
  }
}

export async function fetchAndLoadPolicyAssetsFromTarGz(
  url: string
): Promise<GatekeeperPolicyAssets> {
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Failed to fetch policy bundle: ${url} (${res.status})`)
  }
  const buf = await res.arrayBuffer()
  const expectedSha = import.meta.env.VITE_GATEKEEPER_BUNDLE_SHA256?.trim()
  if (expectedSha) {
    await assertGatekeeperBundleSha256(buf, expectedSha)
  }
  return loadPolicyAssetsFromTarGzBuffer(buf)
}

export async function loadPolicyAssetsFromStaticPublic(): Promise<GatekeeperPolicyAssets> {
  const base = STATIC_POLICY_BASE
  const wasmPath = `${base}/policy.wasm`
  const manifestPath = `${base}/.manifest`
  const dataPath = `${base}/data.json`
  const regoPath = STATIC_REGO_PATH

  const [wasmResponse, manifestResponse, dataResponse, regoResponse] =
    await Promise.all([
      fetch(wasmPath),
      fetch(manifestPath),
      fetch(dataPath),
      fetch(regoPath),
    ])

  if (!wasmResponse.ok) {
    throw new Error(`Failed to load policy asset: ${wasmPath}`)
  }
  if (!manifestResponse.ok) {
    throw new Error(`Failed to load policy asset: ${manifestPath}`)
  }
  if (!dataResponse.ok) {
    throw new Error(`Failed to load policy asset: ${dataPath}`)
  }

  const [wasm, manifestJson, dataJson] = await Promise.all([
    wasmResponse.arrayBuffer(),
    manifestResponse.json() as Promise<Record<string, unknown>>,
    dataResponse.json() as Promise<Record<string, unknown>>,
  ])
  patchManifestModulePath(manifestJson)

  let rego: string | null = null
  if (regoResponse.ok) {
    rego = await regoResponse.text()
  }

  return {
    wasm,
    manifest: manifestJson,
    data: dataJson,
    rego,
    loadedAt: Date.now(),
  }
}
